import { AssetVizMode } from '@asset-viz/defines'
import { CoordinateInputTablePointAndClickApi } from 'components/form/inputs/coordinatesInput/CoordinatesInputTable'
import { Coordinate } from 'components/form/inputs/coordinatesInput/helpers/coordinateTranslations'
import {
  workflowDeclaresPanel,
  type WorkflowScopedProviderDescriptor,
} from 'features/libra/workflow/workflowScopedProviderTypes'
import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react'
import {
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoFeature,
  GeckoMarkupLayer,
  RolodexEntryTagsType,
} from 'types'

import { buildCoordinateMarkupLayers } from './ffsCoordinateMarkup'

const DEFAULT_ASSET_VIZ_MODE = AssetVizMode.AllFeaturesSelectable

export interface FfsWorkspaceStateValue {
  /** Single source of truth for asset viz selection. `selectedFeatureId` is derived. */
  selection: GeckoFeature | GeckoComponentCoords | undefined
  setSelection: (value: GeckoFeature | GeckoComponentCoords | undefined) => void

  /**
   * Asset viz interaction mode. Read-only externally -- flipped internally by
   * `pointAndClickApi.enterPointSelectionMode` / `exitPointSelectionMode`.
   */
  assetVizMode: AssetVizMode

  /** Imperative coord-display API. Asset model step binds component tags at the call site. */
  coordinateDisplay: {
    display: (
      coords: ReadonlyArray<Coordinate>,
      tags: RolodexEntryTagsType
    ) => void
    clear: () => void
  }

  /** PointAndClickApi shape consumed by the asset model editor's coord input table. */
  pointAndClickApi: CoordinateInputTablePointAndClickApi

  /** Ambient FFS markup layers produced by the active step. */
  ffsMarkupLayers: ReadonlyArray<GeckoMarkupLayer>
  setFfsMarkupLayers: (layers: ReadonlyArray<GeckoMarkupLayer>) => void

  /**
   * The currently selected indication in the triage step. Drives the bottom
   * detail drawer AND a highlight style on the matching viz markup rect.
   * `null` when no indication is selected (drawer closed).
   */
  selectedIndicationId: string | null
  setSelectedIndicationId: (id: string | null) => void

  /**
   * Markup-layer id (e.g. a ruler id) the detail panel asked the viz to zoom/
   * fit to. `FfsAssetVizPanel` subscribes, frames the matching layer, and
   * resets this to null. `null` when there is no pending zoom request.
   */
  zoomToLayerId: string | null
  setZoomToLayerId: (id: string | null) => void

  /** Markup layers produced by `coordinateDisplay`. Viz panel concatenates with FFS layers. */
  coordinateMarkupLayers: ReadonlyArray<GeckoMarkupLayer>

  /**
   * The ticket's primary CDF -- single source of truth for the active component
   * data frame. The viz panel sets this when its CDF list loads (or when the
   * user changes selection via the built-in selector) and uses it as
   * `ForecastAssetVizWindow.selectedCDF`. The triage step reads it as a
   * fallback CDF for ruler coord conversion when an indication has no
   * matching CTP CDF.
   */
  primaryCdf: GeckoComponentDataFrame | undefined
  setPrimaryCdf: (cdf: GeckoComponentDataFrame | undefined) => void
}

const FfsWorkspaceStateContext = createContext<FfsWorkspaceStateValue | null>(
  null
)

export const FfsWorkspaceStateProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [selection, setSelection] = useState<
    GeckoFeature | GeckoComponentCoords | undefined
  >(undefined)
  const [assetVizMode, setAssetVizMode] = useState<AssetVizMode>(
    DEFAULT_ASSET_VIZ_MODE
  )
  const [ffsMarkupLayers, setFfsMarkupLayers] = useState<
    ReadonlyArray<GeckoMarkupLayer>
  >([])
  const [coordinateMarkupLayers, setCoordinateMarkupLayers] = useState<
    ReadonlyArray<GeckoMarkupLayer>
  >([])
  const [primaryCdf, setPrimaryCdf] = useState<
    GeckoComponentDataFrame | undefined
  >(undefined)
  const [selectedIndicationId, setSelectedIndicationId] = useState<
    string | null
  >(null)
  const [zoomToLayerId, setZoomToLayerId] = useState<string | null>(null)

  const coordinateDisplay = useMemo(
    () => ({
      display: (
        coords: ReadonlyArray<Coordinate>,
        tags: RolodexEntryTagsType
      ) => {
        setCoordinateMarkupLayers(buildCoordinateMarkupLayers(coords, tags))
      },
      clear: () => {
        setCoordinateMarkupLayers([])
      },
    }),
    []
  )

  // pointAndClickApi mirrors AssetModelEditorExtension.tsx:161-190.
  const pointAndClickApi = useMemo<CoordinateInputTablePointAndClickApi>(() => {
    if (assetVizMode === AssetVizMode.SelectPoint) {
      const selectedPoint =
        selection instanceof GeckoComponentCoords ? selection : undefined
      return {
        isInPointSelectionMode: true,
        exitPointSelectionMode: () => {
          setAssetVizMode(DEFAULT_ASSET_VIZ_MODE)
        },
        selectedPoint,
        clearSelection: () => {
          setSelection(undefined)
        },
      }
    }
    return {
      isInPointSelectionMode: false,
      enterPointSelectionMode: () => {
        setSelection(undefined)
        setAssetVizMode(AssetVizMode.SelectPoint)
      },
    }
  }, [assetVizMode, selection])

  const value = useMemo<FfsWorkspaceStateValue>(
    () => ({
      selection,
      setSelection,
      assetVizMode,
      coordinateDisplay,
      pointAndClickApi,
      ffsMarkupLayers,
      setFfsMarkupLayers,
      coordinateMarkupLayers,
      primaryCdf,
      setPrimaryCdf,
      selectedIndicationId,
      setSelectedIndicationId,
      zoomToLayerId,
      setZoomToLayerId,
    }),
    [
      assetVizMode,
      coordinateDisplay,
      coordinateMarkupLayers,
      pointAndClickApi,
      primaryCdf,
      selectedIndicationId,
      zoomToLayerId,
      selection,
      ffsMarkupLayers,
    ]
  )

  return (
    <FfsWorkspaceStateContext.Provider value={value}>
      {children}
    </FfsWorkspaceStateContext.Provider>
  )
}

export function useFfsWorkspaceState(): FfsWorkspaceStateValue {
  const ctx = useContext(FfsWorkspaceStateContext)
  if (!ctx) {
    throw new Error(
      'useFfsWorkspaceState must be used within a FfsWorkspaceStateProvider. ' +
        'The provider is injected per-workflow via the workflowScopedProviders ' +
        'registry -- if a new FFS step component consumes this hook, add its ' +
        'component name to FFS_WORKSPACE_STATE_CONSUMER_STEPS in ' +
        'FfsWorkspaceStateContext.tsx.'
    )
  }
  return ctx
}

/**
 * FFS step components (by workflow-step `component` name) that consume
 * `useFfsWorkspaceState` -- directly or through `useFfsMarkupLayers` /
 * `FfsAssetModelEditor`. The registry predicate below mounts the provider
 * for any workflow declaring one of these steps, so the hook's throw can
 * never fire for a configured workflow. New FFS steps that consume the
 * workspace state MUST be added here.
 */
export const FFS_WORKSPACE_STATE_CONSUMER_STEPS: ReadonlySet<string> = new Set([
  'FfsAssetModelStep',
  'FfsMinimumThicknessStep',
  'FfsBinningStep',
  // `FfsIndicationTriageStep` is retained during the additive rollout; it is
  // superseded by the two part-scoped steps below and removed in cleanup once
  // no workflow JSON references it.
  'FfsIndicationTriageStep',
  'FfsPartFourStep',
  'FfsPartFiveStep',
])

/**
 * Registry descriptor: the libra shell injects the workspace-state provider
 * (via `WorkflowScopedProviders placement='workspace'`, i.e. wrapping each
 * workspace layout render below the step/tab boundaries -- same lifetime it
 * always had) for workflows that either declare an FFS workspace-state
 * consuming step or render the FFS viz panel in any step layout.
 */
export const ffsWorkspaceStateScopedProvider: WorkflowScopedProviderDescriptor =
  {
    key: 'ffs-workspace-state',
    placement: 'workspace',
    appliesTo: (workflow) =>
      workflow.steps.some((step) =>
        FFS_WORKSPACE_STATE_CONSUMER_STEPS.has(step.component)
      ) || workflowDeclaresPanel(workflow, 'FfsAssetVizPanel'),
    Provider: FfsWorkspaceStateProvider,
  }
